import { useEffect, useRef, useState, type JSX } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, Thumbs, EffectFade } from "swiper/modules";
import SwiperCore from "swiper";
import { ProgressPagination } from "./ProgressPagination";
import {
    FixedElMobile,
    MarketingCarouselArrowSvg,
    MarketingCarouselArrowsWrapper,
    MarketingCarouselMainWrapper,
    MarketingCarouselSlidesWrapper
} from "./MarketingCarousel.styles";
import { useInView } from "react-intersection-observer";

export type DotsType = {
    icon: string;
    text: string;
}[];

type MarketingCarouselProps = {
    dotComponents: DotsType;
    slidesComponents: JSX.Element[];
    paginationWidth: number;
    carouselMaxWidth?: number;
    fixedEl?: JSX.Element;
    effect?: "slide" | "fade";
    contentMarginTop?: number;
    shrinkLength: number;
    lastElementWidth: number;
    autoHeight?: boolean;
};

function addExtraProps(Component: JSX.Element, extraProps: any) {
    return <Component.type {...Component.props} {...extraProps} />;
}

export const MarketingCarousel = ({
    dotComponents,
    slidesComponents,
    paginationWidth,
    fixedEl,
    carouselMaxWidth,
    effect,
    contentMarginTop = 40,
    shrinkLength,
    lastElementWidth,
    autoHeight = false
}: MarketingCarouselProps) => {
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | undefined>(undefined);
    const [transitionDuration, setTransitionDuration] = useState(7000);
    const [hasStarted, setHasStarted] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const numberOfSlides = dotComponents.length;
    const slides = Array.from(Array(numberOfSlides).keys());
    const usesFixedEl = typeof fixedEl !== "undefined";
    const containerEl = useRef<HTMLDivElement>(null);
    const { ref, inView } = useInView({
        threshold: 0.8,
        root: containerEl.current,
        initialInView: false
    });

    const transitionSpeed = 600;

    useEffect(() => {
        thumbsSwiper?.autoplay?.stop && thumbsSwiper?.autoplay.stop();
    }, [thumbsSwiper]);

    useEffect(() => {
        if (inView && thumbsSwiper && !thumbsSwiper.autoplay.running && !hasStarted) {
            setActiveIndex(0);
            setTransitionDuration(6999);
            thumbsSwiper?.autoplay.start();
            setHasStarted(true);
        }
    }, [inView]);

    return (
        <div ref={ref}>
            {usesFixedEl && <FixedElMobile>{fixedEl}</FixedElMobile>}
            <ProgressPagination
                numberSlides={numberOfSlides}
                activeIndex={activeIndex}
                paginationWidth={paginationWidth}
                onSelectIndex={(index: number) => {
                    if (thumbsSwiper) {
                        thumbsSwiper.slideTo(index);
                        thumbsSwiper?.autoplay?.stop && thumbsSwiper.autoplay.stop();
                        setTransitionDuration(7001);
                    }
                }}
                transitionDuration={transitionDuration}
                transitionDelay={transitionSpeed}
                dotComponents={dotComponents}
                shrinkLength={shrinkLength}
                autoplayRunning={thumbsSwiper?.autoplay?.running}
                lastElementWidth={lastElementWidth}
            />
            <MarketingCarouselMainWrapper maxWidth={carouselMaxWidth}>
                <MarketingCarouselSlidesWrapper usesFixedEl={usesFixedEl} contentMarginTop={contentMarginTop}>
                    <div>{fixedEl}</div>
                    <div>
                        <Swiper
                            onSwiper={setThumbsSwiper}
                            onActiveIndexChange={(swiper) => {
                                setActiveIndex(swiper.activeIndex);
                            }}
                            effect={effect}
                            speed={transitionSpeed}
                            slidesPerView={1}
                            fadeEffect={{
                                crossFade: true
                            }}
                            modules={[Navigation, Pagination, Autoplay, Thumbs, EffectFade]}
                            spaceBetween={50}
                            autoplay={{
                                delay: transitionDuration,
                                stopOnLastSlide: true
                            }}
                            autoHeight={autoHeight}
                        >
                            {slides.map((index) => (
                                <SwiperSlide key={index}>
                                    {addExtraProps(slidesComponents[index], {
                                        onRightArrowCLick: () => {
                                            thumbsSwiper && thumbsSwiper.slideNext();
                                        },
                                        onLeftArrowCLick: () => {
                                            thumbsSwiper && thumbsSwiper.slidePrev();
                                        },
                                        canTriggerLeftAction: activeIndex > 0,
                                        canTriggerRightAction: activeIndex < slides.length - 1
                                    })}
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </MarketingCarouselSlidesWrapper>
                <MarketingCarouselArrowsWrapper>
                    <div>
                        <MarketingCarouselArrowSvg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 59.414 59.414"
                            width="40px"
                            onClick={() => {
                                thumbsSwiper && thumbsSwiper.slidePrev();
                            }}
                            canTriggerAction={activeIndex > 0}
                        >
                            <path d="M45.268 1.414 43.854 0 14.146 29.707l29.708 29.707L45.268 58 16.975 29.707z" />
                        </MarketingCarouselArrowSvg>
                    </div>
                    <div>
                        <MarketingCarouselArrowSvg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 47.255 47.255"
                            width="40px"
                            onClick={() => {
                                thumbsSwiper && thumbsSwiper.slideNext();
                            }}
                            canTriggerAction={activeIndex < slides.length - 1}
                        >
                            <path d="M12.314 47.255a.999.999 0 0 1-.707-1.707l21.92-21.92-21.92-21.92A.999.999 0 1 1 13.021.294L35.648 22.92a.999.999 0 0 1 0 1.414L13.021 46.962a.999.999 0 0 1-.707.293z" />
                        </MarketingCarouselArrowSvg>
                    </div>
                </MarketingCarouselArrowsWrapper>
            </MarketingCarouselMainWrapper>
        </div>
    );
};
